import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Parse from "parse";
import { useNotify } from "react-admin";
import { initializeWebSocket } from "../../../Provider/socket";
import {
  getMessages,
  getMessagingStatus,
  getMessagableRoles,
  getUsersWithMessages,
  markRead,
  searchUsers,
  sendMsg,
} from "./messagingService";

export const useUserListMessaging = () => {
  const notify = useNotify();
  const currentUser = Parse.User.current();
  const searchTimerRef = useRef(null);
  const messagingStatusRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [messagingEnabled, setMessagingEnabled] = useState(true);

  const handleMessagingDisabled = useCallback(() => {
    messagingStatusRef.current = false;
    setMessagingEnabled(false);
    setOpen(false);
    setSelectedUser(null);
    setMessages([]);
    setChatUsers([]);
    setRoleOptions([]);
    setSearchResults([]);
  }, []);

  const isMessagingDisabledError = useCallback(
    (error) => error?.message?.toLowerCase?.().includes("messaging disabled"),
    []
  );

  const loadMessagingStatus = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && typeof messagingStatusRef.current === "boolean") {
      return messagingStatusRef.current;
    }

    try {
      const enabled = await getMessagingStatus();
      messagingStatusRef.current = enabled;
      setMessagingEnabled(enabled);
      if (!enabled) {
        handleMessagingDisabled();
      }
      return enabled;
    } catch (error) {
      console.error("Failed to load messaging status", error);
      return true;
    }
  }, [handleMessagingDisabled]);

  const loadRoleOptions = useCallback(async () => {
    try {
      if (!(await loadMessagingStatus())) return;
      const roles = await getMessagableRoles();
      setRoleOptions(roles || []);
    } catch (error) {
      if (isMessagingDisabledError(error)) {
        handleMessagingDisabled();
        return;
      }
      console.error("Failed to load message roles", error);
    }
  }, [handleMessagingDisabled, isMessagingDisabledError, loadMessagingStatus]);

  const loadUsers = useCallback(async () => {
    try {
      if (!(await loadMessagingStatus())) return;
      setLoadingUsers(true);
      const data = await getUsersWithMessages();
      setChatUsers(data || []);
    } catch (error) {
      if (isMessagingDisabledError(error)) {
        handleMessagingDisabled();
        return;
      }
      notify(error.message || "Failed to load conversations", {
        type: "error",
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [handleMessagingDisabled, isMessagingDisabledError, loadMessagingStatus, notify]);

  const loadMessages = useCallback(
    async (user) => {
      if (!user) return;

      try {
        if (!(await loadMessagingStatus())) return;
        setLoadingMessages(true);
        const data = await getMessages(user);
        setMessages(data || []);
      } catch (error) {
        if (isMessagingDisabledError(error)) {
          handleMessagingDisabled();
          return;
        }
        notify(error.message || "Failed to load messages", { type: "error" });
      } finally {
        setLoadingMessages(false);
      }
    },
    [handleMessagingDisabled, isMessagingDisabledError, loadMessagingStatus, notify]
  );

  const selectUser = useCallback(
    async (user) => {
      setSelectedUser(user);
      await loadMessages(user);
      await markRead(user);
      await loadUsers();
    },
    [loadMessages, loadUsers]
  );

  const send = useCallback(async () => {
    if (!text.trim() || !selectedUser) return;
    if (!(await loadMessagingStatus())) return;

    const trimmed = text.trim();
    const tempMessage = {
      id: `temp-${Date.now()}`,
      senderId: currentUser?.id,
      text: trimmed,
      message: trimmed,
      createdAt: new Date(),
      isRead: true,
      isMe: true,
    };

    setMessages((prev) => [...prev, tempMessage]);
    setText("");
    setSending(true);

    try {
      const result = await sendMsg(selectedUser, trimmed);

      if (window.socket?.connected && currentUser?.id) {
        window.socket.emit(
          "new_message",
          JSON.stringify({
            recipientId: selectedUser.userId || selectedUser.id,
            senderId: currentUser.id,
            senderUsername: currentUser.get("username"),
            message: trimmed,
          })
        );
      }

      setMessages((prev) =>
        prev.map((item) =>
          item.id === tempMessage.id
            ? { ...item, id: result?.messageId || item.id }
            : item
        )
      );
      await loadUsers();
    } catch (error) {
      if (isMessagingDisabledError(error)) {
        handleMessagingDisabled();
        return;
      }
      notify(error.message || "Failed to send message", { type: "error" });
      await loadMessages(selectedUser);
    } finally {
      setSending(false);
    }
  }, [
    currentUser,
    handleMessagingDisabled,
    isMessagingDisabledError,
    loadMessages,
    loadMessagingStatus,
    loadUsers,
    notify,
    selectedUser,
    text,
  ]);

  const sendDirectMessage = useCallback(
    async (user, messageText) => {
      if (!user || !messageText?.trim()) return null;
      if (!(await loadMessagingStatus())) return null;

      const trimmed = messageText.trim();
      let result = null;

      try {
        result = await sendMsg(user, trimmed);
      } catch (error) {
        if (isMessagingDisabledError(error)) {
          handleMessagingDisabled();
          return null;
        }
        throw error;
      }

      if (window.socket?.connected && currentUser?.id) {
        window.socket.emit(
          "new_message",
          JSON.stringify({
            recipientId: user.userId || user.id,
            senderId: currentUser.id,
            senderUsername: currentUser.get("username"),
            message: trimmed,
          })
        );
      }

      await loadUsers();
      await selectUser(user);
      return result;
    },
    [
      currentUser,
      handleMessagingDisabled,
      isMessagingDisabledError,
      loadMessagingStatus,
      loadUsers,
      selectUser,
    ]
  );

  useEffect(() => {
    initializeWebSocket();
  }, []);

  useEffect(() => {
    loadMessagingStatus();
  }, [loadMessagingStatus]);

  useEffect(() => {
    const handleMessagingStatusChanged = async (event) => {
      const enabledFromEvent = event?.detail?.enabled;

      if (typeof enabledFromEvent === "boolean") {
        messagingStatusRef.current = enabledFromEvent;
        setMessagingEnabled(enabledFromEvent);

        if (!enabledFromEvent) {
          handleMessagingDisabled();
          return;
        }
      }

      const enabled = await loadMessagingStatus(true);
      if (enabled) {
        await loadRoleOptions();
        await loadUsers();
      }
    };

    window.addEventListener(
      "messaging-status-changed",
      handleMessagingStatusChanged
    );

    return () => {
      window.removeEventListener(
        "messaging-status-changed",
        handleMessagingStatusChanged
      );
    };
  }, [handleMessagingDisabled, loadMessagingStatus, loadRoleOptions, loadUsers]);

  useEffect(() => {
    if (!messagingEnabled) return;
    loadUsers();
  }, [loadUsers, messagingEnabled]);

  useEffect(() => {
    if (!messagingEnabled) return;
    loadRoleOptions();
  }, [loadRoleOptions, messagingEnabled]);

  useEffect(() => {
    if (!messagingEnabled || !window.socket || !currentUser?.id) return;

    if (window.socket.connected) {
      window.socket.emit(
        "join_messaging",
        JSON.stringify({ userId: currentUser.id })
      );
    }

    const handleConnect = () => {
      window.socket.emit(
        "join_messaging",
        JSON.stringify({ userId: currentUser.id })
      );
    };

    const handleIncoming = async (payload) => {
      try {
        const data =
          typeof payload === "string" ? JSON.parse(payload) : payload || {};

        await loadUsers();

        const activeUserId = selectedUser?.userId || selectedUser?.id;
        if (activeUserId && activeUserId === data.senderId) {
          await loadMessages(selectedUser);
        } else {
          notify(`New message from ${data.senderUsername || "User"}`, {
            type: "info",
          });
        }
      } catch (error) {
        console.error("Failed to handle incoming message", error);
      }
    };

    window.socket.on("connect", handleConnect);
    window.socket.on("message_received", handleIncoming);

    return () => {
      window.socket?.off("connect", handleConnect);
      window.socket?.off("message_received", handleIncoming);
    };
  }, [currentUser, loadMessages, loadUsers, messagingEnabled, notify, selectedUser]);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);

    if (!messagingEnabled) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    if (!search.trim()) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    searchTimerRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const users = await searchUsers(search);
        setSearchResults(users || []);
      } catch (error) {
        if (isMessagingDisabledError(error)) {
          handleMessagingDisabled();
          return;
        }
        notify(error.message || "Failed to search users", { type: "error" });
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(searchTimerRef.current);
  }, [handleMessagingDisabled, isMessagingDisabledError, messagingEnabled, notify, search]);

  const filteredChatUsers = useMemo(() => {
    return chatUsers.filter((user) => {
      const matchesSearch = search.trim()
        ? user.username?.toLowerCase().includes(search.trim().toLowerCase())
        : true;

      const matchesRole = roleFilter ? user.role === roleFilter : true;

      const messageDate = user.lastMessageAt ? new Date(user.lastMessageAt) : null;
      const matchesFromDate = fromDate
        ? messageDate && messageDate >= new Date(`${fromDate}T00:00:00`)
        : true;
      const matchesToDate = toDate
        ? messageDate && messageDate <= new Date(`${toDate}T23:59:59`)
        : true;

      return matchesSearch && matchesRole && matchesFromDate && matchesToDate;
    });
  }, [chatUsers, fromDate, roleFilter, search, toDate]);

  const usersToRender = useMemo(() => {
    if (!search.trim()) return filteredChatUsers;

    const existingIds = new Set(filteredChatUsers.map((user) => user.userId || user.id));
    const combined = [...filteredChatUsers];

    searchResults.forEach((user) => {
      const userId = user.userId || user.id;
      if (!existingIds.has(userId)) {
        combined.push(user);
      }
    });

    return combined;
  }, [filteredChatUsers, search, searchResults]);

  const totalUnread = useMemo(
    () => chatUsers.reduce((sum, user) => sum + (user.unread || 0), 0),
    [chatUsers]
  );

  const clearFilters = useCallback(() => {
    setSearch("");
    setRoleFilter("");
    setFromDate("");
    setToDate("");
    setSearchResults([]);
  }, []);

  return {
    open,
    setOpen,
    chatUsers: usersToRender,
    selectedUser,
    messages,
    text,
    setText,
    search,
    setSearch,
    roleFilter,
    setRoleFilter,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    selectUser,
    send,
    totalUnread,
    clearFilters,
    loadingUsers,
    loadingMessages,
    searching,
    sending,
    roleOptions,
    sendDirectMessage,
    messagingEnabled,
  };
};
